import { addBreadcrumb, captureException, Severity } from '@sentry/react';
import axios, { AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';
import config from 'config';
import { getTokens } from 'modules/oauth/tokenStore';
import {
  InteractiveSession,
  SessionEnded,
  SessionEnding,
  SessionEndReason,
  SessionStarted,
  SessionTimeRemaining,
  SessionUpdated
} from './types/InteractiveSession';

type Method = 'PUT' | 'POST' | 'PATCH' | 'DELETE';
const MAX_RETRY = 3;
export class RepliconSession extends EventTarget implements InteractiveSession {
  public remainingTime: SessionTimeRemaining | null;
  private applicationRootUrl: string | null = null;
  public isInitialized = false;
  public useSessionV2 = false;

  constructor() {
    super();
    this.remainingTime = null;
  }

  update(
    remainingTime: SessionTimeRemaining | null
  ): SessionTimeRemaining | null {
    if (!remainingTime) {
      this.remainingTime = null;
    } else {
      this.remainingTime = remainingTime;

      this.dispatchEvent(new SessionUpdated(this.remainingTime));
    }

    return this.remainingTime;
  }

  dispatchEnd(reason: SessionEndReason = SessionEndReason.expired): void {
    this.dispatchEvent(new SessionEnding(reason));

    this.applicationRootUrl = null;
    this.remainingTime = null;
    this.isInitialized = false;

    this.dispatchEvent(new SessionEnded(reason));
  }

  async request(
    method: Method
  ): Promise<{ remainingTime: SessionTimeRemaining | null; headers?: any }> {
    const tokens = getTokens();
    try {
      const resp = await this.getSession({
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`,
          ...(method !== 'PUT' && this.applicationRootUrl
            ? { 'x-application-root-url': this.applicationRootUrl as string }
            : {})
        }
      });

      const appRootUrl = resp.headers['x-application-root-url'];
      if (appRootUrl) {
        this.applicationRootUrl = appRootUrl;
      }

      return {
        remainingTime: resp.data as SessionTimeRemaining,
        headers: resp.headers
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const ecid = error.response.headers['x-execution-correlation-id'];
        addBreadcrumb({
          type: 'http',
          category: `session.${method}`,
          data: { ecid: ecid, status: error.response.status },
          level: Severity.Info
        });

        if (error.response.status === 401) {
          this.dispatchEnd(SessionEndReason.expired);
          return { remainingTime: null };
        }

        if (error.response.status === 409) {
          this.dispatchEnd(SessionEndReason.different_user);
          return { remainingTime: null };
        }
      }
      captureException(error);
      return { remainingTime: null };
    }
  }

  async begin(): Promise<SessionTimeRemaining | null> {
    this.remainingTime = null;
    this.isInitialized = true;
    const { remainingTime, headers } = await this.request('PUT');
    const actualUser = headers?.['x-replicon-actual-user-id'];
    this.dispatchEvent(new SessionStarted(actualUser, remainingTime));
    return this.update(remainingTime);
  }

  async extend(): Promise<SessionTimeRemaining | null> {
    const { remainingTime } = await this.request('PATCH');

    return this.update(remainingTime);
  }

  async remaining(): Promise<SessionTimeRemaining | null> {
    const { remainingTime } = await this.request('POST');

    return this.update(remainingTime);
  }

  async validate(): Promise<boolean> {
    const allowedDomain = config.allowedSessionDomains.split(',');
    if (
      window.REPLICON_SKIP_SESSION ||
      !allowedDomain.some((domain) => window.location.hostname.endsWith(domain))
    ) {
      return false;
    }
    try {
      const resp = await this.getSession({ method: 'POST' });

      return resp.status === 200;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const ecid = error.response.headers['x-execution-correlation-id'];
        addBreadcrumb({
          type: 'http',
          category: 'session.validate',
          data: { ecid: ecid, status: error.response.status },
          level: Severity.Info
        });

        if (error.response.status === 401) {
          this.dispatchEnd(SessionEndReason.expired);
          return false;
        }

        if (error.response.status === 409) {
          this.dispatchEnd(SessionEndReason.no_substitutes);
          return false;
        }
      }
      captureException(error);
      return false;
    }
  }

  async end(reason: SessionEndReason = SessionEndReason.logout): Promise<void> {
    await this.request('DELETE');

    this.dispatchEnd(reason);
  }

  async getSession({
    method,
    headers,
    tryCount = 1
  }: {
    method: Method;
    headers?: { [key: string]: string };
    tryCount?: number;
  }): Promise<AxiosResponse<any>> {
    try {
      const resp = await axios.request({
        url: this.useSessionV2 ? '/api/session/v2' : '/api/session',
        method: method,
        headers
      });
      return resp;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const ecid = err.response?.headers['x-execution-correlation-id'];
        Sentry.addBreadcrumb({
          type: 'http',
          category: method,
          data: {
            ecid,
            url: err.config.url,
            status: err.response?.status,
            headers: JSON.stringify(err.response?.headers)
          },
          level: Severity.Error
        });
        Sentry.captureMessage(`Session ${method} failed`);
      }
      if (
        axios.isAxiosError(err) &&
        err.response?.status === 503 &&
        tryCount < MAX_RETRY
      ) {
        await this.getSession({ method, headers, tryCount: ++tryCount });
      }
      throw err;
    }
  }
}
